import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Anchor, ArrowRight, Eye, BatteryCharging, CloudLightning, Headset,
  ShieldCheck, Compass, Camera, MapPin, CheckCircle2, Clock,
} from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { CtaBand } from "@/components/CtaBand";
import { Seo } from "@/components/Seo";
import { DIFFERENTIATORS, SERVICES, TIERS, REGIONS, FAQS, BRAND } from "@/data/content";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

const ICONS = { Eye, BatteryCharging, CloudLightning, Headset, ShieldCheck, Compass };

export default function Home() {
  const navigate = useNavigate();
  return (
    <div data-testid="home-page">
      <Seo
        title="Hussar Marine Concierge — Dockside Vessel Oversight in Southwest Florida"
        description="Independent dockside vessel inspection and oversight for absentee and seasonal boat owners across Southwest Florida. Your Vessel. Our Vigilance."
      />

      {/* HERO */}
      <section className="relative min-h-screen flex items-center bg-navy-900 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center animate-fade-in"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1745167554647-a02b5011140b?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000)",
          }}
        />
        <div className="absolute inset-0 hero-scrim" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 w-full pt-28 pb-16">
          <div className="max-w-3xl">
            <Reveal>
              <p className="eyebrow text-gold-400 mb-6 flex items-center gap-3">
                <span className="h-px w-10 bg-gold-500" /> {BRAND.serviceArea} Marine Concierge
              </p>
            </Reveal>
            <Reveal delay={120}>
              <h1 className="text-4xl sm:text-6xl lg:text-7xl font-serif font-bold text-white leading-[1.05] text-shadow-navy">
                Your Vessel.
                <span className="block gold-shimmer">Our Vigilance.</span>
              </h1>
            </Reveal>
            <Reveal delay={240}>
              <p className="mt-7 text-lg text-slate-200 max-w-xl leading-relaxed">
                Discreet, documented dockside oversight for absentee and seasonal
                owners. Scheduled inspections, timestamped photographs, and clear
                reporting — so your boat is watched while you're away.
              </p>
            </Reveal>
            <Reveal delay={360}>
              <div className="mt-9 flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => navigate("/request-inspection")}
                  className="inline-flex items-center justify-center gap-2 bg-gold-500 hover:bg-gold-400 text-navy-900 font-semibold px-8 py-4 rounded-full transition-all hover:shadow-[0_12px_32px_-10px_rgba(212,175,55,0.7)]"
                  data-testid="hero-request-button"
                >
                  <Anchor className="h-5 w-5" /> Request an Inspection
                </button>
                <button
                  onClick={() => navigate("/pricing")}
                  className="inline-flex items-center justify-center gap-2 border border-white/25 text-white hover:border-gold-500/60 hover:text-gold-400 font-semibold px-8 py-4 rounded-full transition-colors"
                  data-testid="hero-pricing-button"
                >
                  Explore Plans <ArrowRight className="h-5 w-5" />
                </button>
              </div>
            </Reveal>
            <Reveal delay={480}>
              <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-slate-300">
                <span className="flex items-center gap-2"><Camera className="h-4 w-4 text-gold-500" /> Timestamped photo reports</span>
                <span className="flex items-center gap-2"><Clock className="h-4 w-4 text-gold-500" /> Scheduled dockside visits</span>
                <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-gold-500" /> Independent & discreet</span>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* DIFFERENTIATORS */}
      <section className="py-20 lg:py-32 bg-ivory dark:bg-navy-800">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="max-w-2xl">
            <Reveal>
              <p className="eyebrow text-gold-600 dark:text-gold-400 mb-4">What We Do</p>
              <h2 className="text-3xl sm:text-4xl font-serif font-bold text-navy-900 dark:text-white">
                Attentive oversight, documented every visit.
              </h2>
              <p className="mt-4 text-slate-600 dark:text-slate-300">
                HMC provides visual condition monitoring and coordination — not repairs
                or vessel operation. Every service is built around clear, timestamped reporting.
              </p>
            </Reveal>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
            {DIFFERENTIATORS.map((d, i) => {
              const Icon = ICONS[d.icon] || Eye;
              return (
                <Reveal key={d.title} delay={i * 90}>
                  <div className="card-lift h-full bg-white dark:bg-navy-900 border border-gold-500/20 rounded-2xl p-7 sm:p-8">
                    <div className="h-12 w-12 rounded-xl bg-navy-900 dark:bg-gold-500/10 flex items-center justify-center">
                      <Icon className="h-6 w-6 text-gold-500" />
                    </div>
                    <h3 className="mt-5 text-xl font-serif font-semibold text-navy-900 dark:text-white">{d.title}</h3>
                    <p className="mt-3 text-slate-600 dark:text-slate-300 leading-relaxed">{d.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-20 lg:py-32 bg-white dark:bg-navy-900">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <Reveal>
            <p className="eyebrow text-gold-600 dark:text-gold-400 mb-4 text-center">Our Services</p>
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-navy-900 dark:text-white text-center">
              Three ways we keep watch
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-7">
            {SERVICES.map((s, i) => {
              const Icon = ICONS[s.icon] || Compass;
              return (
                <Reveal key={s.slug} delay={i * 110}>
                  <Link
                    to={`/services/${s.slug}`}
                    className="card-lift group block h-full rounded-2xl overflow-hidden border border-slate-200 dark:border-white/10 bg-ivory dark:bg-navy-800"
                    data-testid={`home-service-${s.slug}`}
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img src={s.hero} alt={s.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      <div className="absolute inset-0 bg-gradient-to-t from-navy-900/80 to-transparent" />
                      <div className="absolute bottom-4 left-5 flex items-center gap-2">
                        <Icon className="h-5 w-5 text-gold-400" />
                        <span className="font-serif text-xl text-white">{s.name}</span>
                      </div>
                    </div>
                    <div className="p-7">
                      <p className="text-sm font-medium text-gold-600 dark:text-gold-400">{s.tagline}</p>
                      <p className="mt-3 text-slate-600 dark:text-slate-300 leading-relaxed">{s.summary}</p>
                      <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-navy-900 dark:text-white group-hover:gap-2.5 transition-all">
                        Learn more <ArrowRight className="h-4 w-4" />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* PRICING PREVIEW */}
      <section className="py-20 lg:py-32 bg-ivory dark:bg-navy-800">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto">
              <p className="eyebrow text-gold-600 dark:text-gold-400 mb-4">Monthly Plans</p>
              <h2 className="text-3xl sm:text-4xl font-serif font-bold text-navy-900 dark:text-white">
                Oversight that scales with your vessel
              </h2>
              <p className="mt-4 text-slate-600 dark:text-slate-300">
                Transparent monthly plans, with recurring payments processed securely
                by Stripe.
              </p>
            </div>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-7 items-stretch">
            {TIERS.map((t, i) => (
              <Reveal key={t.id} delay={i * 110}>
                <div
                  className={`card-lift relative h-full rounded-2xl p-8 flex flex-col ${
                    t.popular
                      ? "bg-navy-900 border-2 border-gold-500 shadow-2xl"
                      : "bg-white dark:bg-navy-900 border border-gold-500/20"
                  }`}
                  data-testid={t.testid}
                >
                  {t.popular && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gold-500 text-navy-900 text-xs font-bold px-4 py-1 rounded-full eyebrow">
                      {t.badge}
                    </span>
                  )}
                  <h3 className={`font-serif text-2xl font-semibold ${t.popular ? "text-white" : "text-navy-900 dark:text-white"}`}>
                    {t.name}
                  </h3>
                  <div className="mt-4 flex items-end gap-1">
                    <span className={`text-4xl font-bold ${t.popular ? "text-gold-400" : "text-navy-900 dark:text-white"}`}>${t.price}</span>
                    <span className={`mb-1 text-sm ${t.popular ? "text-slate-400" : "text-slate-500 dark:text-slate-400"}`}>/ month</span>
                  </div>
                  <p className={`mt-4 text-sm ${t.popular ? "text-slate-300" : "text-slate-600 dark:text-slate-300"}`}>{t.description}</p>
                  <ul className="mt-6 space-y-3 flex-1">
                    {t.features.slice(0, 4).map((f) => (
                      <li key={f} className={`flex items-start gap-2.5 text-sm ${t.popular ? "text-slate-200" : "text-slate-600 dark:text-slate-300"}`}>
                        <CheckCircle2 className="h-4 w-4 text-gold-500 mt-0.5 shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/request-inspection"
                    className={`mt-7 inline-flex items-center justify-center gap-2 font-semibold px-6 py-3 rounded-full transition-all ${
                      t.popular
                        ? "bg-gold-500 hover:bg-gold-400 text-navy-900"
                        : "border border-navy-900/20 dark:border-white/20 text-navy-900 dark:text-white hover:border-gold-500"
                    }`}
                  >
                    Get Started
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <p className="text-center mt-8">
              <Link to="/pricing" className="text-sm font-semibold text-navy-900 dark:text-gold-400 inline-flex items-center gap-1.5 hover:gap-2.5 transition-all">
                Compare full plans <ArrowRight className="h-4 w-4" />
              </Link>
            </p>
          </Reveal>
        </div>
      </section>

      {/* SERVICE REGIONS */}
      <section className="py-20 lg:py-28 bg-white dark:bg-navy-900">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <p className="eyebrow text-gold-600 dark:text-gold-400 mb-4">Service Area</p>
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-navy-900 dark:text-white">
              Proposed coverage across Southwest Florida
            </h2>
            <p className="mt-4 text-slate-700 dark:text-slate-300 max-w-lg">
              We are establishing dockside oversight from Naples to Charlotte Harbor.
              Coverage is being rolled out progressively and is subject to availability
              and marina authorization at each location.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-3">
              {REGIONS.map((r) => (
                <div key={r} className="flex items-center gap-2.5 text-slate-700 dark:text-slate-200">
                  <MapPin className="h-4 w-4 text-gold-500 shrink-0" />
                  <span className="text-sm font-medium">{r}</span>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={150}>
            <div className="relative rounded-3xl overflow-hidden h-[420px] shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1631655104117-f1cfdf688772?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                alt="Southwest Florida marina at sunset"
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-900/60 to-transparent" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 lg:py-28 bg-ivory dark:bg-navy-800">
        <div className="max-w-3xl mx-auto px-6 sm:px-8">
          <Reveal>
            <p className="eyebrow text-gold-600 dark:text-gold-400 mb-4 text-center">Questions</p>
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-navy-900 dark:text-white text-center">
              Clear answers, no surprises
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <Accordion type="single" collapsible className="mt-10" data-testid="home-faq">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-b border-slate-200 dark:border-white/10">
                  <AccordionTrigger className="text-left font-serif text-lg text-navy-900 dark:text-white hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 dark:text-slate-300 leading-relaxed">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      <CtaBand />
    </div>
  );
}
